import { useEffect, useState } from "react";
import InputTextComp from "@components/UI/InputText/Index";
import { formatValueCompliance } from "@validations/Compliance/FormatValue";
import { useFetchByCode } from "@hooks/api/Compliance/useFetchByCode";
import { useFormContext } from "react-hook-form";
import { etapaProcessoOptions } from "@enums/EtapasProcesso/Index.jsx";
import SelectOptionComp from "@components/UI/SelectOption/Index";
import { tipoComplianceOptions } from "@enums/Compliance/Index";
import { flagAuditoriaOptions } from "@enums/FlagAuditoria/Index";
import { Link } from "react-router-dom";
import ButtonComp from "@components/UI/Button/Index";
import { formatDateToBR } from "@utils/FormatDate";

const Associacao = ({
  compliance,
  setCompliance,
  openModal,
}) => {
  const { register } = useFormContext();
  const [fields, setFields] = useState({
    tipoCompliance: "",
    ano: "",
    tipoServico: "",
    tipoServicoAssociado: "",
    nomeProcesso: "",
    flgAuditoria: null,
    dataCriacao: "",
    etapaProcesso: ""
  });
  const hoverMessage = "Campo preenchido automaticamente pelo sistema";
  const { fetchByCode } = useFetchByCode();

  // Preenche campos locais quando selectedRow muda
  useEffect(() => {
    const row = compliance.selectedRow;

    if (row) {
      setFields({
        tipoCompliance: tipoComplianceOptions.find(opt => opt.value === row.tipoCompliance)?.label,
        ano: row.ano,
        tipoServico: row.tipoServico,
        tipoServicoAssociado: row.tipoServicoAssociado,
        nomeProcesso: row.nomeProcesso,
        flgAuditoria: flagAuditoriaOptions.find(opt => opt.value === row.flagAuditoria)?.label,
        dataCriacao: row.dataCriacao
      });
      // Seta etapa default apenas se não estiver preenchida
      if (!compliance.etapaProcesso) {
        setCompliance(prev => ({
          ...prev,
          etapaProcesso: "NAO_APLICADO",
          idProcesso: row.idProcesso,
        }));
      }
    } else {
      setFields({
        tipoCompliance: "",
        ano: "",
        tipoServico: "",
        tipoServicoAssociado: "",
        nomeProcesso: "",
        flgAuditoria: "",
        dataCriacao: ""
      });
    }
    // eslint-disable-next-line
  }, [compliance.selectedRow]);

  // Limpa selectedRow quando código fica vazio
  useEffect(() => {
    if (!compliance.codigoCompliance?.trim()) {
      setCompliance(prev => ({
        ...prev,
        selectedRow: null,
        idProcesso: "",
        etapaProcesso: "",
        nomeProcesso: "",
        flgAuditoria: "",
        dataCriacao: ""
      }));
    }
    // eslint-disable-next-line
  }, [compliance.codigoCompliance]);

  // Handler para campo código
  const handleCodigoChange = (e) => {
    const value = formatValueCompliance(e.target.value);
    setCompliance(prev => ({
      ...prev,
      codigoCompliance: value,
    }));
    if (!value.trim()) {
      setCompliance(prev => ({
        ...prev,
        selectedRow: null,
        idProcesso: "",
        etapaProcesso: "",
        nomeProcesso: "",
        flgAuditoria: "",
        dataCriacao: ""
      }));
    }
  };

  // Busca compliance pelo código ou abre modal
  const handleModalCompliances = async () => {
    if (!compliance.codigoCompliance?.trim()) {
      openModal();
      return;
    }
    const item = await fetchByCode(compliance.codigoCompliance);
    if (item) {
      setCompliance(prev => ({
        ...prev,
        selectedRow: item,
        idProcesso: item.idProcesso,
        nomeProcesso: item.nomeProcesso,
        etapaProcesso: prev.etapaProcesso || "NAO_APLICADO",
        flgAuditoria: item.flgAuditoria,
        dataCriacao: item.dataCriacao
      }));
    }
  };

return (
  <div className="form">
    <div className="container-fluid p-0">

      {/* Linha 1: Código + Botão + Link */}
      <div className="row mt-3">

        {/* Campo Código */}
        <div className="col-12 col-md-4 p-0">
          <InputTextComp
            label="Código"
            value={compliance.codigoCompliance}
            {...register("codigoCompliance")}
            onChange={handleCodigoChange}
            placeholder="Digite ou Busque"
            hoverMessage="Digite apenas letras e números ou busque pelo ícone"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleModalCompliances();
              }
            }}
            required
          />
        </div>

        {/* Botão + Link */}
        <div className="col-12 col-md-8 d-flex align-items-center gap-3">
          <ButtonComp
            title="Buscar"
            type="button"
            buttonColor="#2b97be"
            onClick={handleModalCompliances}
            classnameIcon="fa-solid fa-magnifying-glass"
            hoverMessage="Abrir lista de compliances"
          />
          <small>
            Não encontrou?{" "}
            <Link
              to="/Compliance"
              className="text-decoration-underline"
              style={{ color: '#2b97be' }}
            >
              Criar um novo compliance
            </Link>
          </small>
        </div>
      </div>

      {/* Linha 2: Demais campos */}
      <div className="row">

        {/* Coluna Esquerda */}
        <div className="col-md-4 p-0">
          <InputTextComp
            label="Compliance"
            value={fields.tipoCompliance}
            placeholder="-"
            hoverMessage={hoverMessage}
            required
            disabled
          />

          <InputTextComp
            label="Ano"
            value={fields.ano}
            placeholder="-"
            hoverMessage={hoverMessage}
            required
            disabled
          />

          <InputTextComp
            label="É uma auditoria?"
            value={fields.flgAuditoria}
            placeholder="-"
            hoverMessage={hoverMessage}
            required
            disabled
          />

          <InputTextComp
            label="Data Criação"
            value={formatDateToBR(fields.dataCriacao)}
            placeholder="-"
            hoverMessage={hoverMessage}
            required
            disabled
          />
        </div>

        {/* Coluna Direita */}
        <div className="col-md-8">
          <InputTextComp
            label="Serviço"
            value={fields.tipoServico}
            placeholder="-"
            hoverMessage={hoverMessage}
            required
            disabled
          />

          <InputTextComp
            label="Tipo"
            value={fields.tipoServicoAssociado}
            placeholder="-"
            hoverMessage={hoverMessage}
            required
            disabled
          />

          <InputTextComp
            label="Processo"
            value={fields.nomeProcesso}
            placeholder="-"
            hoverMessage={hoverMessage}
            required
            disabled
          />

          <SelectOptionComp
            label="Etapa do Processo"
            options={etapaProcessoOptions}
            value={etapaProcessoOptions.find(opt => opt.value === compliance.etapaProcesso)}
            onChange={selected => {
              const etapa = selected?.value || selected;
              if (compliance.selectedRow?.idProcesso && etapa) {
                setCompliance(prev => ({
                  ...prev,
                  etapaProcesso: etapa,
                  idProcesso: compliance.selectedRow.idProcesso,
                }));
              }
            }}
            placeholder={!compliance.codigoCompliance ? "-" : "Selecionar uma etapa do processo"}
            hoverMessage="Selecionar uma etapa do processo"
            required
            isDisabled={!compliance.codigoCompliance}
          />
        </div>
      </div>
    </div>
  </div>
);


};

export default Associacao;
